import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Sparkles, ArrowLeft, Loader2, Heart, Share2, Check, Copy, FileText, Trash2 } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import TarotCard from '../components/TarotCard';
import SiteFooter from '../components/SiteFooter';
import { format } from 'date-fns';
import { toast } from 'sonner';

const ReadingDetail = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [reading, setReading] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchReading();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchReading = async () => {
    try {
      const response = await axios.get(`${API}/readings/${id}`);
      setReading(response.data);
      setNotes(response.data.notes || '');
      if (response.data.share_id) {
        setShareUrl(`${window.location.origin}/share/${response.data.share_id}`);
      }
    } catch (error) {
      toast.error('Failed to load reading');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async () => {
    try {
      const response = await axios.post(`${API}/readings/${id}/favorite`);
      setReading({ ...reading, is_favorite: response.data.is_favorite });
      toast.success(response.data.is_favorite ? 'Added to favorites' : 'Removed from favorites');
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.put(`${API}/readings/${id}/notes`, { notes });
      toast.success('Notes saved');
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const generateShareLink = async () => {
    try {
      const response = await axios.post(`${API}/readings/${id}/share`);
      const url = `${window.location.origin}/share/${response.data.share_id}`;
      setShareUrl(url);
      toast.success('Share link ready below');
      // Try to copy, but don't fail if clipboard is unavailable
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      } catch (_clipErr) {
        // Clipboard not available - URL is still shown in UI
      }
    } catch (error) {
      toast.error('Failed to generate share link');
    }
  };

  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Could not copy');
    }
  };

  const downloadAsImage = async () => {
    // Use browser print as a simple PDF/image alternative; html2canvas would be heavier
    toast.success('Use your browser\'s "Save as PDF" option in the print dialog');
    setTimeout(() => window.print(), 500);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/readings/${id}`);
      toast.success('Reading deleted');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to delete reading');
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const getSpreadLabel = (type) => ({
    'single': 'Single Card',
    'three-card': 'Three Card',
    'five-card': 'Five Card',
    'celtic-cross': 'Celtic Cross',
    'daily': 'Daily Reading',
  }[type] || type);

  const getPositionLabel = (index, spreadType) => {
    if (spreadType === 'three-card') return ['Past', 'Present', 'Future'][index];
    if (spreadType === 'five-card') return ['Core Issue', 'Past Influence', 'Emerging Future', 'External Influence', 'Best Path'][index];
    if (spreadType === 'single' || spreadType === 'daily') return 'Insight';
    if (spreadType === 'celtic-cross') return `Position ${index + 1}`;
    return '';
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#F8F9FA]">
      <nav className="glass-effect border-b border-white/10 print:hidden">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
            className="flex items-center gap-2 text-zinc-400 hover:text-[#D4AF37] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            <span>Dashboard</span>
          </button>
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="text-2xl font-heading font-light">Seatask Arcane AI</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12" data-testid="reading-detail-page">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin" />
          </div>
        ) : reading ? (
          <div>
            <div className="text-center mb-12">
              <p className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-4">
                {format(new Date(reading.created_at), 'EEEE, MMMM d, yyyy')}
              </p>
              <h1 className="text-5xl font-heading font-light tracking-tighter mb-4">
                {getSpreadLabel(reading.spread_type)}
              </h1>
              {reading.question && (
                <p className="text-[#D4AF37] text-lg italic">"{reading.question}"</p>
              )}

              {/* Action buttons */}
              <div className="flex justify-center gap-3 mt-6 print:hidden">
                <button
                  onClick={toggleFavorite}
                  data-testid="favorite-button"
                  className="flex items-center gap-2 px-4 py-2 border border-white/10 rounded-md hover:border-[#D4AF37]/50 transition-all text-sm"
                >
                  <Heart
                    className={`w-4 h-4 ${reading.is_favorite ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-zinc-400'}`}
                    strokeWidth={1.5}
                  />
                  {reading.is_favorite ? 'Favorited' : 'Favorite'}
                </button>
                <button
                  onClick={generateShareLink}
                  data-testid="share-button"
                  className="flex items-center gap-2 px-4 py-2 border border-white/10 rounded-md hover:border-[#D4AF37]/50 transition-all text-sm"
                >
                  <Share2 className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
                  Share
                </button>
                <button
                  onClick={downloadAsImage}
                  data-testid="download-button"
                  className="flex items-center gap-2 px-4 py-2 border border-white/10 rounded-md hover:border-[#D4AF37]/50 transition-all text-sm"
                >
                  <FileText className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
                  Save as PDF
                </button>
                <button
                  onClick={() => setConfirmDelete(true)}
                  data-testid="delete-reading-button"
                  className="flex items-center gap-2 px-4 py-2 border border-white/10 rounded-md hover:border-red-500/50 hover:text-red-400 transition-all text-sm text-zinc-400"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  Delete
                </button>
              </div>

              {/* Share URL display */}
              {shareUrl && (
                <div className="mt-4 max-w-lg mx-auto flex items-center gap-2 glass-effect rounded-md p-2 print:hidden" data-testid="share-url-box">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 bg-transparent text-zinc-300 text-xs px-2 focus:outline-none"
                  />
                  <button
                    onClick={copyShareUrl}
                    className="p-2 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded transition-colors"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              )}
            </div>

            <div className="flex flex-wrap justify-center gap-6 mb-12">
              {reading.cards.map((card, index) => (
                <div key={index} className="w-52 sm:w-56 lg:w-64 text-center">
                  <TarotCard card={card} isRevealed={true} />
                  <div className="mt-3">
                    <p className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-1">
                      {getPositionLabel(index, reading.spread_type)}
                    </p>
                    <p className="text-sm font-heading">{card.name}</p>
                    {card.is_reversed && <p className="text-xs text-[#D4AF37]">Reversed</p>}
                  </div>
                </div>
              ))}
            </div>

            <div className="glass-effect rounded-lg p-8 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-heading font-medium">Interpretation</h2>
                {reading.ai_powered === false && (
                  <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 border border-white/10 px-2 py-1 rounded-full" title="Generated from card keywords (AI temporarily offline)">
                    Classic Reading
                  </span>
                )}
              </div>
              {reading.interpretation && reading.interpretation.includes('currently unavailable') ? (
                <div className="mb-4 p-4 rounded-md border border-amber-500/30 bg-amber-500/5">
                  <p className="text-sm text-amber-400">
                    ⚠ This reading was saved during an early AI outage. Create a new reading for a full interpretation.
                  </p>
                </div>
              ) : null}
              <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{reading.interpretation}</p>
            </div>

            {/* Personal Notes */}
            <div className="glass-effect rounded-lg p-8 print:hidden">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-heading font-medium">Your Notes</h2>
                <button
                  onClick={saveNotes}
                  disabled={savingNotes}
                  data-testid="save-notes-button"
                  className="px-4 py-1.5 bg-[#D4AF37] text-[#050505] rounded-md hover:bg-[#F3E5AB] transition-all text-sm font-medium disabled:opacity-50"
                >
                  {savingNotes ? 'Saving...' : 'Save'}
                </button>
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="notes-textarea"
                placeholder="Add personal reflections, thoughts, or insights about this reading..."
                rows={5}
                className="w-full bg-[#0F0F11] border border-white/10 rounded-md p-4 text-[#F8F9FA] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40 resize-none"
              />
            </div>
          </div>
        ) : null}
      </div>

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 bg-[#050505]/80 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => !deleting && setConfirmDelete(false)}
          data-testid="confirm-delete-modal"
        >
          <div
            className="glass-effect rounded-lg max-w-md w-full p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-2xl font-heading font-medium mb-3">Delete this reading?</h3>
            <p className="text-zinc-400 text-sm mb-6">This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                data-testid="confirm-cancel"
                className="px-5 py-2 border border-white/10 text-zinc-300 rounded-md hover:border-[#D4AF37]/50 transition-all text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                data-testid="confirm-delete"
                className="px-5 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-all text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      <SiteFooter className="print:hidden" />
    </div>
  );
};

export default ReadingDetail;
